(() => {
  const app = document.querySelector('[data-pc-app]');
  if (!app) return;

  const form = app.querySelector('[data-pc-form]');
  const summaryEl = app.querySelector('[data-pc-summary]');
  const outputEl = app.querySelector('[data-pc-output]');
  const unitEl = app.querySelector('[data-pc-unit]');
  const weightEl = form.elements.weight;

  const KG_PER_LB = 0.45359237;
  const REFERENCE_FAT = 25;
  const OLDER_AGE = 65;
  // Same g/kg targets as the amino acid calculator, plus weight loss (Leidy et al., 2015).
  const GOALS = {
    sedentary: { factor: 1.2, low: 1.2, high: 1.6, lifting: false, basis: 'for everyday health above the RDA (Phillips et al., 2016)' },
    active: { factor: 1.4, low: 1.4, high: 2.0, lifting: false, basis: 'for active adults (ISSN, Jäger et al., 2017)' },
    loss: { factor: 1.6, low: 1.2, high: 1.6, lifting: false, basis: 'for weight loss, the top of the 1.2–1.6 g/kg range in a review of weight-loss trials (Leidy et al., 2015)' },
    strength: { factor: 1.6, low: 1.6, high: 2.2, lifting: true, basis: 'for strength training, where muscle gains plateau in a meta-analysis of 49 trials (Morton et al., 2018)' },
    cut: { factor: 2.2, low: 2.0, high: 2.6, lifting: true, basis: 'for lifting in a calorie deficit (ISSN, Jäger et al., 2017)' },
  };
  const SCOOPS = { unflavored: 28, flavored: 22 };
  const PLACEHOLDER = {
    imperial: app.dataset.pcPlaceholderImperial || '150',
    metric: app.dataset.pcPlaceholderMetric || '68',
  };

  const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
  const num = (value) => {
    const n = parseFloat(String(value ?? '').replace(',', '.'));
    return Number.isFinite(n) ? n : NaN;
  };
  const grams = (value) => Math.round(value).toLocaleString('en-US');
  const pct = (ratio) => `${Math.round(ratio * 100)}%`;

  function readForm() {
    const data = new FormData(form);
    return {
      units: data.get('units') === 'metric' ? 'metric' : 'imperial',
      age: num(data.get('age')),
      weight: num(data.get('weight')),
      bodyFat: num(data.get('bodyFat')),
      bodyFatText: String(data.get('bodyFat') || '').trim(),
      goal: GOALS[data.get('goal')] ? data.get('goal') : 'active',
    };
  }

  function profileFor(input) {
    if (!(input.age > 0) || !(input.weight > 0)) {
      return { error: 'Enter your age and weight to see how much protein you need a day.' };
    }
    if (input.age < 18) return { error: 'This calculator is designed for adults 18 and older.' };
    if (input.age > 110) return { error: 'Check your age.' };
    const weight = input.units === 'metric' ? input.weight : input.weight * KG_PER_LB;
    if (weight < 30 || weight > 300) {
      return { error: `Check your weight. It should be between ${input.units === 'metric' ? '30 and 300 kg' : '66 and 660 lb'}.` };
    }
    const bodyFat = input.bodyFat >= 3 && input.bodyFat <= 70 ? input.bodyFat : null;
    const adjusted = bodyFat !== null && bodyFat > REFERENCE_FAT;
    const refWeight = adjusted ? (weight * (1 - bodyFat / 100)) / (1 - REFERENCE_FAT / 100) : weight;
    const goal = GOALS[input.goal];
    const older = input.age >= OLDER_AGE;
    const target = goal.factor * refWeight;
    return {
      weight,
      refWeight,
      adjusted,
      bodyFatIgnored: input.bodyFatText !== '' && bodyFat === null,
      goal,
      older,
      target,
      low: goal.low * refWeight,
      high: goal.high * refWeight,
      rda: 0.8 * refWeight,
      mealMinimum: (older || goal.lifting ? 0.4 : 0.3) * refWeight,
    };
  }

  const weightText = (kg, units) => (units === 'metric' ? `${Math.round(kg * 10) / 10} kg` : `${Math.round(kg / KG_PER_LB)} lb`);
  const kgText = (kg, units) => (units === 'metric' ? `${Math.round(kg * 10) / 10} kg` : `${Math.round(kg)} kg (${Math.round(kg / KG_PER_LB)} lb)`);

  function render() {
    const input = readForm();
    unitEl.textContent = input.units === 'metric' ? 'kg' : 'lb';
    weightEl.placeholder = PLACEHOLDER[input.units];

    const profile = profileFor(input);
    if (profile.error) {
      summaryEl.textContent = profile.error;
      outputEl.innerHTML = '';
      return;
    }

    const { goal, target, refWeight } = profile;
    const perMeal = target / 3;
    const perPound = goal.factor * KG_PER_LB;
    summaryEl.textContent = `Aim for about ${grams(target)} g of protein a day: about ${grams(perMeal)} g at each of 3 meals.`;

    const notes = [
      `${goal.factor} g per kg × ${kgText(refWeight, input.units)} = ${grams(target)} g a day (${perPound.toFixed(2)} g per pound), ${goal.basis}.`,
    ];
    if (profile.adjusted) {
      notes.push(`Based on lean mass: at ${input.bodyFat}% body fat, your weight at 25% body fat with the same muscle would be ${weightText(refWeight, input.units)}.`);
    } else if (profile.bodyFatIgnored) {
      notes.push('Body fat should be between 3% and 70%, so it wasn’t used.');
    } else if (input.bodyFatText === '' && profile.weight >= 90) {
      notes.push('If you carry more body fat, adding a body fat estimate gives a lower target based on lean mass.');
    }
    notes.push(`Get at least ${grams(profile.mealMinimum)} g in each main meal${profile.older ? ', since muscle needs more protein per meal from 65' : goal.lifting ? ' to support muscle when you lift' : ''}.`);
    if (profile.older) {
      notes.push('From 65, expert groups recommend at least 1.0–1.2 g per kg a day (PROT-AGE, ESPEN); every goal here is at or above that.');
    }

    const scoopShare = SCOOPS.unflavored / target;
    const bag = outputEl.dataset.pcBag;
    const shop = outputEl.dataset.pcShop || '/collections/20-servings';

    outputEl.innerHTML = `
      <div class="cantein-aa__panel cantein-aa__protein">
        <h3>Daily protein target</h3>
        <div class="cantein-aa__protein-top">
          <p class="cantein-aa__big">${grams(target)}<small>g / day</small></p>
          <dl class="cantein-aa__stats">
            <dt>Range</dt><dd>${grams(profile.low)}–${grams(profile.high)} g</dd>
            <dt>Per meal (3)</dt><dd>${grams(perMeal)} g</dd>
            <dt>RDA minimum</dt><dd>${grams(profile.rda)} g</dd>
          </dl>
        </div>
        <ul class="cantein-pc__notes">${notes.map((note) => `<li>${esc(note)}</li>`).join('')}</ul>
      </div>
      <div class="cantein-aa__panel cantein-pc__scoop">
        ${bag ? `<img src="${esc(bag)}" alt="Cantein organic unflavored hemp protein bag, 700g" width="120" height="120" loading="lazy">` : ''}
        <div>
          <h3>Where a scoop fits</h3>
          <p>One 35 g scoop of Cantein Unflavored has ${SCOOPS.unflavored} g of protein, ${pct(scoopShare)} of your day. Vanilla and Chocolate have ${SCOOPS.flavored} g. Unflavored is one ingredient: organic hemp protein.</p>
          <a class="cantein-aa__button" href="${esc(shop)}">Shop Cantein</a>
        </div>
      </div>
      <p class="cantein-pc__next">Want to check the quality of that protein? The <a href="/pages/amino-acid-calculator">amino acid calculator</a> checks a day of food against all nine essential amino acids.</p>`;
  }

  form.addEventListener('input', render);
  form.addEventListener('change', render);
  form.addEventListener('submit', (event) => event.preventDefault());
  render();
})();
